import { Badge, Box, Paper, Tooltip, Text, Title } from '@cowbell/ui';
import Highlight from 'react-highlighter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileLines,
  faClipboardList,
  faCoins,
  faUser,
  faFolder,
  faShieldHalved,
  faGrid2,
  faHelmetSafety,
} from '@fortawesome/pro-light-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import React from 'react';
import { useCmailContext } from './CmailStateProvider';
import { Checkbox } from '../../../components/inputs';
import { Flexbox } from '../../../components/layout/Flexbox';
import { useFileTypeIcons } from '../../../components/fileUploader/useFileTypeIcons';
import { useMeQuery } from '../../../components/hooks/useMeQuery';
import { useQueryParams } from '../../../components/providers/QueryParamsProvider';
import { EmailType } from '../cmail.statics';
import { REDACTED_FIELD } from '../../../config/logrocket';
import { LabelCell } from './LabelCell';
import { useGetLabels } from '../hooks/useGetLabels';
import { FilterType } from './useCmailState';
import { stripHtmlTagsPreserveNewlines } from '../utils/htmlContentUtils';
import { AssignedToCell } from './AssignedToCell';
import type { SenderTrustDecision } from '../../../api/cmail/cmail.dto';
import {
  MeetingSummaryListIcon,
  meetingLookupIds,
  useReMeetingCounts,
} from './CmailDetailsResolver/CmailDetails/MeetingSummaryButton';
import {
  formatFlagName,
  getSenderTrustIcon,
  getTrustLevelIcon,
  getVerifiedUserIcon,
  isNegativeFlag,
} from '../utils/senderTrustUtils';
import { getSubCategoryLabel } from '../utils/tabSubCategories';
import classes from './CmailListing.module.css';

interface CmailListingProps {
  messages: any[];
  isLoading?: boolean;
  mailboxType?: 'INBOX' | 'SENT' | 'DRAFT';
  /** The inbox tab being listed: chips each row with its subcategory there */
  readonly categoryTab?: string;
}

export const Highlighter = ({ children, ...props }: any) => {
  const { query } = useQueryParams();
  if (!query.search) {
    return children;
  }

  return (
    <Highlight matchClass={classes.highlightMatch} {...props}>
      {children}
    </Highlight>
  );
};

export const CmailListing = ({
  messages,
  isLoading,
  mailboxType,
  categoryTab,
}: CmailListingProps) => {
  // One request per page for the meeting-summary icons, not one per row.
  const { data: meetingCounts = {} } = useReMeetingCounts(
    meetingLookupIds(messages, mailboxType)
  );

  if (!isLoading && !messages.length) {
    return (
      <Box display="flex" className={classes.emptyState}>
        <Title order={1}>
          No results were found matching the current search parameters
        </Title>
      </Box>
    );
  }

  return (
    <>
      {messages.map((message: any) => (
        <CmailListItem
          key={message.msgId}
          message={message}
          mailboxType={mailboxType}
          meetingCount={meetingCounts[message.msgId] ?? 0}
          categoryTab={categoryTab}
        />
      ))}
    </>
  );
};

type Message = {
  seenBy: string[];
  msgId: string;
  label: string;
  isStarred?: boolean;
  msgCategory?: string;
  emailSubCategory?: string;
  senderName?: string;
  senderEmail?: string;
  emailReferenceCount?: number;
  subject?: string;
  mainMessage?: string;
  origMessage?: string;
  ui_dateReceivedShort?: string;
  emailType?: string;
  dateReceived?: string;
  attachments?: any[];
  assignedTo?: {
    email: string;
    firstName?: string;
    lastName?: string;
  };
  senderTrustDecision?: SenderTrustDecision;
  userVerified?: boolean;
};

const getEmailTypeDataKey = (type?: string): string => {
  switch (type) {
    case EmailType.REPLY:
      return 'reply';
    case EmailType.FORWARD:
      return 'forward';
    case EmailType.FOLLOWUP:
      return 'followup';
    case EmailType.NEW:
    default:
      return 'new';
  }
};

const getEmailTypeLabel = (type?: string) => {
  switch (type) {
    case EmailType.REPLY:
      return 'R';
    case EmailType.FORWARD:
      return 'FW';
    case EmailType.FOLLOWUP:
      return 'F';
    case EmailType.NEW:
    default:
      return 'N';
  }
};

const getEmailTypeTooltip = (type?: string) => {
  switch (type) {
    case EmailType.REPLY:
      return 'Reply';
    case EmailType.FORWARD:
      return 'Forward';
    case EmailType.FOLLOWUP:
      return 'Follow-up';
    case EmailType.NEW:
    default:
      return 'New';
  }
};

const EmailTypeCell = ({ emailType }: { emailType?: string }) => {
  return (
    <Flexbox width="2rem" marginRight="0.5rem" marginLeft="0.5rem">
      <Tooltip label={getEmailTypeTooltip(emailType)} position="top">
        <Box
          className={classes.emailTypeBadge}
          data-type={getEmailTypeDataKey(emailType)}
        >
          {getEmailTypeLabel(emailType)}
        </Box>
      </Tooltip>
    </Flexbox>
  );
};

const CmailListItem = ({
  message,
  mailboxType,
  meetingCount = 0,
  categoryTab,
}: Readonly<{
  message: Message;
  mailboxType?: 'INBOX' | 'SENT' | 'DRAFT';
  meetingCount?: number;
  categoryTab?: string;
}>) => {
  const meQuery = useMeQuery();
  const labelsQuery = useGetLabels();
  const isRead = message.seenBy?.includes(meQuery.data?.email);
  const { selection, goToDetailView, targetMessage } = useCmailContext();
  const isSelected = message.msgId in selection;
  const isActive = message.msgId === targetMessage?.msgId;

  const handleRedirect = () => goToDetailView(message);

  return (
    <Paper
      className={classes.container}
      data-active={isActive}
      data-selected={isSelected}
      shadow={undefined}
      radius={0}
    >
      <Flexbox>
        <CheckboxCell message={message} />
        {mailboxType === 'INBOX' && (
          <TrustLevelCell
            senderTrustDecision={message.senderTrustDecision}
            userVerified={message.userVerified}
          />
        )}
        {mailboxType === 'INBOX' && (
          <LabelCell
            msgId={message.msgId}
            labelValue={message.label}
            options={(() => {
              const apiOptions = labelsQuery.labels || [];
              const enumOptions = [
                {
                  label: 'Incomplete',
                  value: FilterType.INCOMPLETE,
                  meta: undefined,
                },
                {
                  label: 'In Progress',
                  value: FilterType.IN_PROGRESS,
                  meta: undefined,
                },
                {
                  label: 'Escalated',
                  value: FilterType.ESCALATED,
                  meta: undefined,
                },
                {
                  label: 'On Hold',
                  value: FilterType.ON_HOLD,
                  meta: undefined,
                },
                { label: 'Failed', value: FilterType.FAILED, meta: undefined },
                {
                  label: 'Complete',
                  value: FilterType.COMPLETE,
                  meta: undefined,
                },
              ];

              const enumValues = new Set<string>(
                enumOptions.map((opt) => opt.value)
              );

              const additionalApiOptions = apiOptions.filter(
                (opt) => !enumValues.has(opt.value)
              );

              return [
                { label: 'Default', value: '', meta: undefined },
                ...enumOptions,
                ...additionalApiOptions,
              ];
            })()}
          />
        )}
        <CategoryCell msgCategory={message.msgCategory} />
        {mailboxType === 'INBOX' && (
          <EmailTypeCell emailType={message.emailType} />
        )}
        <SenderCell
          senderName={message.senderName}
          senderEmail={message.senderEmail}
          isRead={isRead}
          onClick={handleRedirect}
          emailReferenceCount={message?.emailReferenceCount ?? 0}
        />
        <SummaryCell
          subject={message.subject}
          body={message.mainMessage ?? message.origMessage}
          isRead={isRead}
          onClick={handleRedirect}
          afterSubject={
            meetingCount > 0 ? (
              <MeetingSummaryListIcon
                msgId={message.msgId}
                count={meetingCount}
              />
            ) : null
          }
          subCategoryLabel={
            categoryTab
              ? getSubCategoryLabel(categoryTab, message.emailSubCategory)
              : null
          }
        />
        <Flexbox
          width="2.5rem"
          minWidth="2.5rem"
          justifyContent="flex-end"
          alignItems="center"
          className={classes.attachmentColumn}
        >
          {message.attachments && message.attachments.length > 0 && (
            <>
              {message.attachments.slice(0, 4).map((attachment: any) => (
                <AttachmentListIcon
                  key={attachment.docId}
                  attachment={attachment}
                />
              ))}
              {message.attachments.length > 4 && (
                <Tooltip
                  label={`${message.attachments.length - 4} more attachments`}
                  withArrow
                >
                  <Box className={classes.attachmentOverflow}>
                    +{message.attachments.length - 4}
                  </Box>
                </Tooltip>
              )}
            </>
          )}
        </Flexbox>
        {(mailboxType === 'INBOX' || mailboxType === 'DRAFT') && (
          <AssignedToCell
            msgId={message.msgId}
            assignedTo={message.assignedTo}
          />
        )}
        <SentDateCell
          sentDate={message.ui_dateReceivedShort}
          isRead={isRead}
          onClick={handleRedirect}
          dateReceived={message.dateReceived}
        />
      </Flexbox>
    </Paper>
  );
};

const CheckboxCell = ({ message }: any) => {
  const { selection, onSelect } = useCmailContext();
  const checked = message.msgId in selection;

  const handleSelect = () => {
    onSelect(message);
  };

  return (
    <Flexbox width="4rem">
      <Checkbox checked={checked} onChange={handleSelect} />
    </Flexbox>
  );
};

const TrustLevelCell = ({
  senderTrustDecision,
  userVerified,
}: Readonly<{
  senderTrustDecision?: SenderTrustDecision;
  userVerified?: boolean;
}>) => {
  const display = getSenderTrustIcon(senderTrustDecision, userVerified);

  if (!display) {
    return (
      <Flexbox justifyContent="center" width="3rem">
        <Box />
      </Flexbox>
    );
  }

  const verifiedUser = getVerifiedUserIcon(userVerified);

  const getFlagStatus = (flag: string, value: boolean): boolean => {
    const isFlagNegative = isNegativeFlag(flag);
    return isFlagNegative ? !value : value;
  };

  const getFlagColor = (flagPassed: boolean): string => {
    return flagPassed
      ? 'var(--cmail-trust-green)'
      : 'var(--mantine-color-red-6)';
  };

  const tooltipContent = (
    <Box>
      {senderTrustDecision &&
        (() => {
          const config = getTrustLevelIcon(senderTrustDecision.trustLevel);
          return (
            <Box className={classes.trustTooltipRow}>
              <Box
                className={classes.trustTooltipIconWrap}
                style={{ '--trust-color': config.color } as React.CSSProperties}
              >
                <FontAwesomeIcon
                  icon={config.icon}
                  className={classes.trustTooltipIcon}
                />
              </Box>
              <strong>Trust Level:</strong> {senderTrustDecision.trustLevel}
            </Box>
          );
        })()}
      {verifiedUser && (
        <Box className={classes.trustTooltipRow}>
          <Box
            className={classes.trustTooltipIconWrap}
            style={
              { '--trust-color': verifiedUser.color } as React.CSSProperties
            }
          >
            <FontAwesomeIcon
              icon={verifiedUser.icon}
              className={classes.trustTooltipIcon}
            />
          </Box>
          <strong>Verified User:</strong> {verifiedUser.label}
        </Box>
      )}
      {senderTrustDecision?.alignedDomain && (
        <Box className={classes.trustTooltipAlignedDomain}>
          <strong>Aligned Domain:</strong> {senderTrustDecision.alignedDomain}
        </Box>
      )}
      {senderTrustDecision?.flags && (
        <Box className={classes.trustTooltipFlagsContainer}>
          <Box className={classes.trustTooltipFlagsHeader}>
            Security Checks:
          </Box>
          <Box component="ul" className={classes.trustTooltipFlagsList}>
            {Object.entries(senderTrustDecision.flags).map(([key, value]) => {
              const boolValue = value as boolean;
              const flagPassed = getFlagStatus(key, boolValue);
              const flagColor = getFlagColor(flagPassed);
              return (
                <Box
                  component="li"
                  key={key}
                  className={classes.trustTooltipFlagItem}
                  style={{ '--flag-color': flagColor } as React.CSSProperties}
                >
                  {flagPassed ? '✓' : '✗'} {formatFlagName(key, boolValue)}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <Flexbox justifyContent="center" width="3rem">
      <Tooltip label={tooltipContent} withArrow>
        <Box
          className={classes.trustLevelDisplay}
          aria-label="Sender trust"
          style={{ '--trust-color': display.color } as React.CSSProperties}
        >
          <FontAwesomeIcon
            icon={display.icon}
            className={classes.trustLevelIcon}
          />
        </Box>
      </Tooltip>
    </Flexbox>
  );
};

const SenderCell = ({
  senderName,
  senderEmail,
  isRead,
  onClick,
  emailReferenceCount,
}: any) => {
  const { query } = useQueryParams();
  const senderLabel = senderName ?? senderEmail;
  const truncatedSenderLabel =
    senderLabel?.length > 16 ? `${senderLabel.slice(0, 16)}...` : senderLabel;

  return (
    <Flexbox className={REDACTED_FIELD} width="18rem" onClick={onClick}>
      <Text
        size="sm"
        className={classes.senderText}
        data-unread={!isRead || undefined}
      >
        <Highlighter search={query.search} matchClass={classes.highlightMatch}>
          {truncatedSenderLabel}
        </Highlighter>{' '}
        {emailReferenceCount > 0 && (
          <Text
            component="span"
            size="xs"
            className={classes.emailReferenceCount}
          >
            {emailReferenceCount}
          </Text>
        )}
      </Text>
    </Flexbox>
  );
};

export const getCategoryIcon = (category?: string): IconDefinition => {
  switch (category) {
    case 'Submission':
      return faFileLines;
    case 'LossRuns':
    case 'Subjectivity':
    case 'Renewal':
    case 'RENEWALS':
      return faClipboardList;
    case 'Claims':
    case 'Claimsops':
    case 'Finance':
    case 'FINANCE':
      return faCoins;
    case 'BOR':
    case 'General':
      return faUser;
    case 'Binders':
    case 'MTE':
      return faFolder;
    case 'CRS':
    case 'CertRequest':
    case 'Compliance':
    case 'COMPLIANCE':
      return faShieldHalved;
    case 'Spam':
    case 'Cancellation':
    case 'OTHER':
    case 'Other':
      return faGrid2;
    case 'API':
      return faFileLines;
    case 'RiskEng':
    case 'RISKENG':
      return faHelmetSafety;
    default:
      return faGrid2;
  }
};

export const getCategoryTooltip = (category?: string) => {
  switch (category) {
    case 'Submission':
      return 'Submissions';
    case 'LossRuns':
      return 'LossRuns';
    case 'Claims':
    case 'Claimsops':
      return 'Claims';
    case 'BOR':
      return 'BOR';
    case 'Binders':
      return 'Binders';
    case 'CRS':
      return 'CRS';
    case 'Subjectivity':
      return 'Subjectivity';
    case 'Spam':
      return 'Spam';
    case 'MTE':
      return 'MTE';
    case 'Renewal':
    case 'RENEWALS':
      return 'Renewals';
    case 'Cancellation':
      return 'Cancellation';
    case 'CertRequest':
      return 'Cert Request';
    case 'API':
      return 'API';
    case 'General':
      return 'General';
    case 'RiskEng':
    case 'RISKENG':
      return 'Risk Engineering';
    case 'Finance':
    case 'FINANCE':
      return 'Finance';
    case 'Compliance':
    case 'COMPLIANCE':
      return 'Compliance';
    case 'OTHER':
    case 'Other':
      return 'Other';
    default:
      return category || 'Unknown';
  }
};

const CategoryCell = ({ msgCategory }: any) => {
  const icon = getCategoryIcon(msgCategory);
  const tooltip = getCategoryTooltip(msgCategory);

  return (
    <Flexbox
      width="3rem"
      marginRight="0rem"
      marginLeft="0rem"
      justifyContent="center"
    >
      <Tooltip label={tooltip} position="top">
        <Box className={classes.categoryCellWrap}>
          <FontAwesomeIcon icon={icon} className={classes.categoryIcon} />
        </Box>
      </Tooltip>
    </Flexbox>
  );
};

const SummaryCell = ({
  subject,
  body,
  isRead,
  onClick,
  afterSubject,
  subCategoryLabel,
}: any) => {
  const { query } = useQueryParams();
  const maximumCombinedLength = 150;
  const subjectText = subject || '';

  const bodyText = body ? stripHtmlTagsPreserveNewlines(body) : '';

  const subjectLength = subjectText.length;
  const remainingLengthForBody = maximumCombinedLength - subjectLength;

  let bodyPreview = '';
  if (remainingLengthForBody > 0) {
    bodyPreview = bodyText.slice(0, remainingLengthForBody);
    if (bodyText.length > remainingLengthForBody) {
      bodyPreview += '...';
    }
  }

  return (
    <Flexbox
      width="100%"
      justifyContent="flex-start"
      onClick={onClick}
      className={classes.summaryCellWrap}
    >
      <Box className={classes.summaryContent}>
        {subCategoryLabel && (
          <Badge
            size="sm"
            radius="xs"
            variant="light"
            className={classes.subCategoryChip}
            style={{ textTransform: 'none' }}
          >
            {subCategoryLabel}
          </Badge>
        )}
        <Text
          component="span"
          size="sm"
          className={classes.subjectText}
          data-unread={!isRead || undefined}
          // With a marker after it, a long subject gives way (ellipsis) so the
          // marker stays visible instead of being clipped off the row.
          data-shrinkable={afterSubject ? true : undefined}
        >
          <Highlighter search={query.search}>{subjectText}</Highlighter>
        </Text>
        {afterSubject && (
          // Its own click (and its modal's, which bubbles through the portal)
          // must not also open the email.
          <Box
            component="span"
            className={classes.afterSubject}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            {afterSubject}
          </Box>
        )}
        <Text
          component="span"
          size="xs"
          className={classes.bodyPreviewText}
          data-unread={!isRead || undefined}
        >
          <Highlighter search={query.search}>{bodyPreview}</Highlighter>
        </Text>
      </Box>
    </Flexbox>
  );
};

const SentDateCell = ({ sentDate, isRead, onClick, dateReceived }: any) => {
  const date = new Date(dateReceived);
  const now = new Date();
  const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

  const displayDate =
    date > twentyFourHoursAgo
      ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : sentDate;

  return (
    <Flexbox width="8rem" justifyContent="flex-end" onClick={onClick}>
      <Text
        size="sm"
        className={classes.sentDateText}
        data-unread={!isRead || undefined}
      >
        {displayDate}
      </Text>
    </Flexbox>
  );
};

export const AttachmentListIcon = ({ attachment }: any) => {
  const fileNameParts = attachment.docName?.split('.');
  const fileExtension = fileNameParts?.length > 1 ? fileNameParts.pop() : '';
  const avatar = useFileTypeIcons(fileExtension, {
    width: '1.2rem',
    height: '1.2rem',
  });

  return (
    <Tooltip label={`${attachment.docName} (View in detail)`} withArrow>
      <Box className={classes.attachmentIcon}>{avatar}</Box>
    </Tooltip>
  );
};
